// src/components/dashboard/ProjectDashboard.jsx
import { appConfirm } from '../../services/appDialogs';
import { AppSelect } from '../ui/AppSelect';
import { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, isDomesticOfficialSeedance } from '../../utils/domesticSeedanceCapabilities';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Film,
  Gamepad2,
  ImagePlus,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Plus,
  Search,
  ScrollText,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Users,
  X
} from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import ProjectMemberModal from "./ProjectMemberModal";
import { runTaskToCompletion } from "../../services/taskService";
import { fetchPublicVisualReferences } from "../../services/visualReferenceService";
import { attachVisualStyleReferences, normalizeVisualStyleKey } from "../../utils/visualStyleReferences";
import {
  assertWanxImageInputLimit,
  getCurrentImageProvider,
  isWanxImageProvider,
  normalizeWanxImageQualityTier,
  WANX_IMAGE_MAX_INPUT_IMAGES,
} from "../../utils/wanxImageCapabilities";
import { STYLE_OPTIONS } from "../storyboard/common/Constants";
import {
  getProjectEntryPath,
  getVideoProviderLabel,
  isInteractiveGameProject,
  isNarrationProject,
  isOverseasProjectProfile,
  isRepaintProject,
  isSd25Project,
  isWan3Project,
  normalizeProjectVideoRatio,
  normalizeProjectProductionMode,
  rememberProjectProductionMode,
  PROJECT_DEFAULT_VIDEO_RATIO,
  PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME,
  PROJECT_PRODUCTION_MODE_NARRATION,
  PROJECT_PRODUCTION_MODE_REPAINT,
  PROJECT_PRODUCTION_MODE_SD25,
  PROJECT_PRODUCTION_MODE_STANDARD,
  PROJECT_PRODUCTION_MODE_WAN3,
  PROJECT_VIDEO_RATIOS,
  VIDEO_PROVIDER_ENG_GUAVIDEO,
  VIDEO_PROVIDER_GUAVIDEO,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
  VIDEO_PROVIDER_WANX,
} from "../../utils/projectRuntime";
import {
  VIDEO_PROVIDER_NEWAPI_SD25,
  isSeedance25Provider,
} from "../../utils/seedance25Capabilities";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";
import StudioArtwork from "../ui/StudioArtwork";
import ProjectOperationsSummary from './ProjectOperationsSummary';
import OperationsEntryLink from '../common/OperationsEntryLink';
import { mergeProjectMetadata, PROJECT_OPERATIONS_LABELS, projectOperationsStatus } from '../../utils/projectOperationsSummary';
import "./ProjectDashboard.css";

const ProjectReleaseDialog = lazy(() => import('../operations/ProjectReleaseDialog'));

const FILTERS = [
  { key: "all", label: "全部" },
  { key: "favorite", label: "收藏" },
  ...Object.entries(PROJECT_OPERATIONS_LABELS).map(([key, label]) => ({ key, label })),
  { key: "coverless", label: "无封面" }
];

const COVER_ASSET_TABS = [
  { key: "characters", label: "角色" },
  { key: "scenes", label: "场景" },
  { key: "props", label: "道具" }
];

const isOverseasProject = isOverseasProjectProfile;

const projectMarketMeta = (project) => {
  if (isDomesticOfficialSeedance(project?.videoProvider)) return { label: '国内官方', subLabel: 'Seedance 2.0', color: 'var(--color-accent-secondary)', colorBorder: 'var(--color-border-primary)', colorSoft: 'var(--color-bg-secondary)' };
  if (isWan3Project(project)) {
    return { label: "Wan3.0 项目", subLabel: "万相 3.0", color: "var(--color-accent-primary)", colorBorder: "color-mix(in srgb, var(--color-accent-primary) 30%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-primary) 9%, transparent)" };
  }
  if (isSd25Project(project) || isSeedance25Provider(project?.videoProvider)) {
    return { label: "2.5 项目", subLabel: getVideoProviderLabel(project?.videoProvider || VIDEO_PROVIDER_NEWAPI_SD25), color: "var(--color-accent-primary)", colorBorder: "color-mix(in srgb, var(--color-accent-primary) 30%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-primary) 9%, transparent)" };
  }
  if (project?.videoProvider === VIDEO_PROVIDER_OMINI_SEEDANCE) {
    return { label: "出海", subLabel: "GuaOmini", color: "var(--color-info, #38bdf8)", colorBorder: "color-mix(in srgb, var(--color-info, #38bdf8) 25%, transparent)", colorSoft: "color-mix(in srgb, var(--color-info, #38bdf8) 7%, transparent)" };
  }
  return isOverseasProject(project)
    ? { label: "出海", subLabel: "EngGuaVideo", color: "var(--color-success)", colorBorder: "color-mix(in srgb, var(--color-success) 25%, transparent)", colorSoft: "color-mix(in srgb, var(--color-success) 7%, transparent)" }
    : { label: "国内", subLabel: "GuaVideo", color: "var(--color-accent-secondary)", colorBorder: "color-mix(in srgb, var(--color-accent-secondary) 25%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-secondary) 7%, transparent)" };
};

const toDateInput = (value) => {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  const dateValue = toDateInput(value);
  return dateValue || "未设置";
};

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

const pad2 = (value) => String(value).padStart(2, "0");

const inputFromLocalDate = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const localDateFromInput = (value) => {
  const dateValue = toDateInput(value);
  if (!dateValue) return null;
  const [year, month, day] = dateValue.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const monthKeyFromValue = (value) => {
  const date = localDateFromInput(value) || new Date();
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;
};

const shiftMonthKey = (monthKey, delta) => {
  const date = localDateFromInput(monthKey) || new Date();
  date.setMonth(date.getMonth() + delta);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;
};

const monthLabel = (monthKey) => {
  const date = localDateFromInput(monthKey) || new Date();
  return `${date.getFullYear()}年${date.getMonth() + 1}月`;
};

const buildCalendarDays = (monthKey) => {
  const monthDate = localDateFromInput(monthKey) || new Date();
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const start = new Date(year, month, 1 - new Date(year, month, 1).getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      value: inputFromLocalDate(date),
      day: date.getDate(),
      currentMonth: date.getMonth() === month,
      today: inputFromLocalDate(date) === inputFromLocalDate(new Date())
    };
  });
};

const isProjectOverdue = (project) => {
  return projectOperationsStatus(project) === 'delayed';
};

const cardTextClamp = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden"
};

const ProjectDashboard = ({ userId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const createModalVisibility = useDelayedUnmount(isCreating, 140, 100);
  const resetCreateFormAfterExitRef = useRef(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDesc, setNewProjectDesc] = useState("");
  const [newProjectProductionMode, setNewProjectProductionMode] = useState(PROJECT_PRODUCTION_MODE_STANDARD);
  const [newProjectMarket, setNewProjectMarket] = useState("domestic");
  const [newProjectVideoProvider, setNewProjectVideoProvider] = useState(VIDEO_PROVIDER_GUAVIDEO);
  const [newProjectVideoRatio, setNewProjectVideoRatio] = useState(PROJECT_DEFAULT_VIDEO_RATIO);
  const [newProjectVisualStyleMode, setNewProjectVisualStyleMode] = useState("fixed");
  const [newProjectVisualStyle, setNewProjectVisualStyle] = useState(() => {
    const saved = normalizeVisualStyleKey(localStorage.getItem("gen_style"), "modern_cdrama");
    return saved && saved !== "custom" ? saved : "modern_cdrama";
  });
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [memberModalProject, setMemberModalProject] = useState(null);
  const [releaseModalProject, setReleaseModalProject] = useState(null);
  const [coverModalProject, setCoverModalProject] = useState(null);
  const [coverAssets, setCoverAssets] = useState({ characters: [], scenes: [], props: [] });
  const [coverSelectedRefs, setCoverSelectedRefs] = useState([]);
  const [coverPrompt, setCoverPrompt] = useState("");
  const [coverAssetTab, setCoverAssetTab] = useState("characters");
  const [loadingCoverAssets, setLoadingCoverAssets] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [deadlinePopoverProjectId, setDeadlinePopoverProjectId] = useState(null);
  const [deadlineCalendarMonths, setDeadlineCalendarMonths] = useState({});
  const [visualStyleReferences, setVisualStyleReferences] = useState([]);
  const projectRequestRef = useRef(null);
  const projectRequestSequence = useRef(0);
  const lastBackgroundRefresh = useRef(0);
  const coverImageProvider = getCurrentImageProvider();
  const isWanxCoverProvider = isWanxImageProvider(coverImageProvider);

  const modalOverlay = {
    backgroundColor: "var(--overlay-medium)"
  };

  const fetchProjects = useCallback(({ silent = false, force = false } = {}) => {
    if (projectRequestRef.current?.userId === userId && !force) return projectRequestRef.current.promise;
    projectRequestRef.current?.controller.abort();
    const controller = new AbortController();
    const sequence = ++projectRequestSequence.current;
    const promise = authenticatedFetch(API_HOST + "/api/project/list", {
      headers: { "X-User-ID": userId }, signal: controller.signal,
    }).then(data => {
      if (!controller.signal.aborted && sequence === projectRequestSequence.current) setProjects(data || []);
    }).catch(error => {
      if (!controller.signal.aborted && sequence === projectRequestSequence.current && !silent) {
        console.error(error);
        toast.error("项目列表加载失败", 0);
      }
    }).finally(() => {
      if (projectRequestRef.current?.sequence === sequence) projectRequestRef.current = null;
    });
    projectRequestRef.current = { userId, controller, sequence, promise };
    return promise;
  }, [userId, toast]);

  useEffect(() => {
    fetchProjects();
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastBackgroundRefresh.current < 3000) return;
      lastBackgroundRefresh.current = Date.now();
      fetchProjects({ silent: true });
    };
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      projectRequestRef.current?.controller.abort();
      projectRequestRef.current = null;
      projectRequestSequence.current += 1;
    };
  }, [fetchProjects]);

  useEffect(() => {
    if (newProjectProductionMode === PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME) {
      setVisualStyleReferences([]);
      return undefined;
    }
    let cancelled = false;
    fetchPublicVisualReferences({ category: "visual_style", mediaType: "image", market: newProjectMarket })
      .then((data) => {
        if (!cancelled) setVisualStyleReferences(data?.items || []);
      })
      .catch((error) => {
        console.warn("[ProjectDashboard] visual style references load failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, [newProjectMarket, newProjectProductionMode]);

  const mergeProject = (updated) => {
    if (!updated?.id) return;
    // A list read started before this successful write can carry old deadline /
    // cover fields. Invalidate it before merging the confirmed write result.
    projectRequestRef.current?.controller.abort();
    projectRequestRef.current = null;
    projectRequestSequence.current += 1;
    setProjects((prev) => prev.map((p) => (p.id === updated.id ? mergeProjectMetadata(p, updated) : p)));
    if (coverModalProject?.id === updated.id) {
      setCoverModalProject((prev) => (prev ? mergeProjectMetadata(prev, updated) : prev));
    }
  };

  const resetNewProjectForm = useCallback(() => {
    setNewProjectName("");
    setNewProjectDesc("");
    setNewProjectProductionMode(PROJECT_PRODUCTION_MODE_STANDARD);
    setNewProjectMarket("domestic");
    setNewProjectVideoProvider(VIDEO_PROVIDER_GUAVIDEO);
    setNewProjectVideoRatio(PROJECT_DEFAULT_VIDEO_RATIO);
    setNewProjectVisualStyleMode("fixed");
  }, []);

  const openCreateModal = useCallback(() => {
    if (resetCreateFormAfterExitRef.current) {
      resetCreateFormAfterExitRef.current = false;
      resetNewProjectForm();
    }
    setIsCreating(true);
  }, [resetNewProjectForm]);

  useEffect(() => {
    if (createModalVisibility.mounted || !resetCreateFormAfterExitRef.current) return undefined;
    const timer = window.setTimeout(() => {
      resetCreateFormAfterExitRef.current = false;
      resetNewProjectForm();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [createModalVisibility.mounted, resetNewProjectForm]);

  const handleCreate = async () => {
    if (!newProjectName.trim()) {
      toast.warning("请输入项目名称");
      return;
    }
    try {
      const requestedProductionMode = normalizeProjectProductionMode(newProjectProductionMode);
      const defaultVideoRatio = normalizeProjectVideoRatio(newProjectVideoRatio);
      const creatingInteractiveGame = requestedProductionMode === PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME;
      const creatingSd25Project = requestedProductionMode === PROJECT_PRODUCTION_MODE_SD25;
      const creatingWan3Project = requestedProductionMode === PROJECT_PRODUCTION_MODE_WAN3;
      const projectMarket = requestedProductionMode === PROJECT_PRODUCTION_MODE_REPAINT
        ? "overseas"
        : creatingInteractiveGame || creatingSd25Project || creatingWan3Project
          ? "domestic"
          : newProjectMarket;
      const videoProvider = requestedProductionMode === PROJECT_PRODUCTION_MODE_REPAINT
        ? VIDEO_PROVIDER_ENG_GUAVIDEO
        : creatingInteractiveGame
          ? VIDEO_PROVIDER_GUAVIDEO
          : creatingSd25Project
            ? ""
            : creatingWan3Project
              ? VIDEO_PROVIDER_WANX
            : newProjectVideoProvider;
      const createdProject = await authenticatedFetch(API_HOST + "/api/project/create", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-ID": userId },
        body: JSON.stringify({
          name: newProjectName,
          description: newProjectDesc,
          productionMode: requestedProductionMode,
          projectMarket,
          contentLanguage: projectMarket === "overseas" ? "en-US" : "zh-CN",
          ...(videoProvider ? { videoProvider } : {}),
          defaultVideoRatio,
          visualStyleMode: creatingInteractiveGame ? "none" : newProjectVisualStyleMode,
          visualStyle: !creatingInteractiveGame && newProjectVisualStyleMode === "fixed" ? newProjectVisualStyle : null,
        })
      });
      let resolvedProject = createdProject?.id
        ? { ...createdProject, defaultVideoRatio }
        : createdProject;
      const returnedProductionMode = normalizeProjectProductionMode(createdProject);
      if (createdProject?.id) {
        rememberProjectProductionMode(createdProject.id, requestedProductionMode);
      }
      if (createdProject?.id && returnedProductionMode !== requestedProductionMode) {
        try {
          const syncedProject = await authenticatedFetch(`${API_HOST}/api/project/update`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", "X-User-ID": userId },
            body: JSON.stringify({
              projectId: createdProject.id,
              productionMode: requestedProductionMode,
            }),
          });
          resolvedProject = syncedProject?.id
            ? { ...createdProject, ...syncedProject, defaultVideoRatio }
            : resolvedProject;
        } catch (syncError) {
          console.warn("[ProjectDashboard] project production mode sync failed", syncError);
        }
      }
      resetCreateFormAfterExitRef.current = true;
      setIsCreating(false);
      await fetchProjects({ force: true });
      if (createdProject?.id) {
        const projectForNavigation = {
          ...(resolvedProject || createdProject),
          productionMode: requestedProductionMode,
          defaultVideoRatio,
          ...(videoProvider ? { videoProvider } : {}),
        };
        try {
          localStorage.setItem(`project_runtime_profile_${createdProject.id}`, JSON.stringify(projectForNavigation));
        } catch {
          // 本地缓存不可用时，仍以服务端保存的项目比例为准。
        }
        navigate(getProjectEntryPath(projectForNavigation, requestedProductionMode), {
          state: {
            projectProductionMode: requestedProductionMode,
            projectDefaultVideoRatio: defaultVideoRatio,
          },
        });
      }
    } catch (e) {
      toast.error("创建失败: " + e.message, 0);
    }
  };

  const handleDelete = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(await appConfirm("确定将该项目移入回收站吗？移入后将不再显示，可联系管理员恢复。", { title: '移入回收站', confirmLabel: '移入回收站' }))) return;
    try {
      await authenticatedFetch(`${API_HOST}/api/project/delete?projectId=${projectId}`, {
        method: "DELETE",
        headers: { "X-User-ID": userId }
      });
      fetchProjects({ force: true });
      toast.success('项目已移入回收站，可联系管理员恢复。');
    } catch {
      toast.error("删除失败", 0);
    }
  };

  const handleFavorite = async (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    const favorite = !project.favorite;
    setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, favorite } : p)));
    try {
      await authenticatedFetch(`${API_HOST}/api/project/favorite`, {
        method: "POST",
        body: JSON.stringify({ projectId: project.id, favorite })
      });
    } catch {
      setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, favorite: project.favorite } : p)));
      toast.error("收藏状态更新失败", 0);
    }
  };

  const handleProjectMetaChange = async (e, project, changes) => {
    e.preventDefault();
    e.stopPropagation();
    const previous = project;
    setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, ...changes } : p)));
    try {
      const updated = await authenticatedFetch(`${API_HOST}/api/project/update`, {
        method: "PUT",
        body: JSON.stringify({ projectId: project.id, ...changes })
      });
      mergeProject(updated);
      if (Object.prototype.hasOwnProperty.call(changes, "deadline")) {
        setDeadlinePopoverProjectId(null);
      }
    } catch (err) {
      setProjects((prev) => prev.map((p) => (p.id === project.id
        ? { ...p, ...Object.fromEntries(Object.keys(changes).map(key => [key, previous[key]])) } : p)));
      toast.error(err.message || "项目信息更新失败", 0);
    }
  };

  const toggleDeadlineCalendar = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    setDeadlineCalendarMonths((prev) => ({
      ...prev,
      [project.id]: prev[project.id] || monthKeyFromValue(project.deadline)
    }));
    setDeadlinePopoverProjectId((current) => (current === project.id ? null : project.id));
  };

  const shiftDeadlineCalendarMonth = (e, project, delta) => {
    e.preventDefault();
    e.stopPropagation();
    setDeadlineCalendarMonths((prev) => ({
      ...prev,
      [project.id]: shiftMonthKey(prev[project.id] || monthKeyFromValue(project.deadline), delta)
    }));
  };

  const openCoverModal = async (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    if (project.currentUserRole !== "owner") return;
    setCoverModalProject(project);
    setCoverSelectedRefs([]);
    setCoverAssetTab("characters");
    const projectFormat = isInteractiveGameProject(project) ? "互动游戏" : "短剧";
    setCoverPrompt(`为${projectFormat}项目《${project.name || "未命名项目"}》生成一张 9:16 竖版封面。
使用我选择的参考图作为人物、场景、道具依据；人物五官、服装、发型、道具和场景气质要贴近参考图。
画面主体明确，适合项目列表封面和${projectFormat}宣传封面。
不要生成文字、标题、logo、水印、字幕或海报字。`);
    setLoadingCoverAssets(true);
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/project/cover-assets?projectId=${project.id}`);
      setCoverAssets(data || { characters: [], scenes: [], props: [] });
    } catch {
      toast.error("封面参考图加载失败", 0);
      setCoverAssets({ characters: [], scenes: [], props: [] });
    } finally {
      setLoadingCoverAssets(false);
    }
  };

  const allCoverAssets = useMemo(() => {
    return [
      ...(coverAssets.characters || []),
      ...(coverAssets.scenes || []),
      ...(coverAssets.props || [])
    ];
  }, [coverAssets]);

  const visibleCoverAssets = useMemo(() => {
    const assets = coverAssets[coverAssetTab] || [];
    return [...assets].sort((a, b) => {
      const aIndex = coverSelectedRefs.findIndex((item) => item.key === a.key);
      const bIndex = coverSelectedRefs.findIndex((item) => item.key === b.key);
      const aSelected = aIndex >= 0;
      const bSelected = bIndex >= 0;
      if (aSelected && bSelected) return aIndex - bIndex;
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      return 0;
    });
  }, [coverAssets, coverAssetTab, coverSelectedRefs]);

  const toggleCoverRef = (asset) => {
    const alreadySelected = coverSelectedRefs.some((item) => item.key === asset.key);
    if (!alreadySelected && isWanxCoverProvider) {
      try {
        assertWanxImageInputLimit(
          [...coverSelectedRefs, asset].map((item) => item.imageUrl),
          coverImageProvider,
        );
      } catch (error) {
        toast.warning(error.message);
        return;
      }
    }
    setCoverSelectedRefs((prev) => {
      const exists = prev.some((item) => item.key === asset.key);
      if (exists) return prev.filter((item) => item.key !== asset.key);
      return [...prev, asset];
    });
  };

  const generateCover = async () => {
    if (!coverModalProject) return;
    if (coverSelectedRefs.length === 0) {
      toast.warning("请至少选择一张参考图");
      return;
    }
    try {
      assertWanxImageInputLimit(
        coverSelectedRefs.map((item) => item.imageUrl),
        coverImageProvider,
      );
    } catch (error) {
      toast.warning(error.message);
      return;
    }
    const imageQualityTier = normalizeWanxImageQualityTier("4K", coverImageProvider);
    setGeneratingCover(true);
    try {
      const { result } = await runTaskToCompletion("PROJECT_GENERATE_COVER", {
          projectId: coverModalProject.id,
          prompt: coverPrompt,
          selectedRefs: coverSelectedRefs,
          aspectRatio: "9:16",
          imageQualityTier,
        }, {
          projectId: coverModalProject.id,
          source: "project_cover",
          provider: coverImageProvider,
          timeoutMs: 30 * 60 * 1000
      });
      const updated = result?.result?.id ? result.result : (result?.id ? result : result?.value);
      if (updated?.id) {
        mergeProject(updated);
        setCoverModalProject(updated);
      } else {
        await fetchProjects({ force: true });
      }
      toast.success("封面已生成");
    } catch (err) {
      toast.error(err.message || "封面生成失败", 0);
    } finally {
      setGeneratingCover(false);
    }
  };

  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return projects.filter((project) => {
      const textMatch = !q
        || String(project.name || "").toLowerCase().includes(q)
        || String(project.description || "").toLowerCase().includes(q);
      if (!textMatch) return false;
      if (filter === "favorite") return !!project.favorite;
      if (Object.hasOwn(PROJECT_OPERATIONS_LABELS, filter)) return projectOperationsStatus(project) === filter;
      if (filter === "coverless") return !project.coverImage;
      return true;
    }).sort((a, b) => {
      if (!!a.favorite !== !!b.favorite) return a.favorite ? -1 : 1;
      return 0;
    });
  }, [projects, searchQuery, filter]);

  const stats = useMemo(() => {
    return {
      total: projects.length,
      favorite: projects.filter((p) => p.favorite).length,
      overdue: projects.filter(isProjectOverdue).length,
      coverless: projects.filter((p) => !p.coverImage).length
    };
  }, [projects]);

  const visualStyleOptions = useMemo(
    () => attachVisualStyleReferences(STYLE_OPTIONS, visualStyleReferences),
    [visualStyleReferences]
  );

  const renderStatusBadge = (project) => {
    const status = projectOperationsStatus(project);
    const label = PROJECT_OPERATIONS_LABELS[status];
    const color = status === 'delayed' ? 'var(--color-error)'
      : ['approved', 'published'].includes(status) ? 'var(--color-success)'
      : status === 'under_review' ? 'var(--color-warning)'
      : status === 'completed' ? theme.colors.accentPrimary
      : 'var(--color-info, #38bdf8)';
    const colorBorder = `color-mix(in srgb, ${color} 27%, transparent)`;
    return (
      <span className="studio-project-status inline-flex items-center gap-1 px-2 py-1 rounded-md text-2xs font-semibold"
        style={{ color, "--project-status-color": color, backgroundColor: "var(--overlay-heavy)", border: `1px solid ${colorBorder}` }}>
        {status === 'delayed' ? <AlertTriangle size={11} /> : ['in_progress', 'under_review'].includes(status) ? <Clock size={11} /> : <CheckCircle2 size={11} />}
        {label}
      </span>
    );
  };

  const renderOwnerControls = (project) => {
    if (project.currentUserRole !== "owner") {
      return <span className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>完成日期：{formatDate(project.deadline)}</span>;
    }
    const calendarMonth = deadlineCalendarMonths[project.id] || monthKeyFromValue(project.deadline);
    const selectedDate = toDateInput(project.deadline);
    const todayValue = inputFromLocalDate(new Date());
    const calendarDays = buildCalendarDays(calendarMonth);
    return (
      <div className="studio-project-owner-controls relative flex flex-wrap items-center gap-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <button
          type="button"
          onClick={(e) => toggleDeadlineCalendar(e, project)}
          className="h-8 rounded-md px-2.5 text-2xs font-mono-num outline-none inline-flex items-center gap-1.5 cursor-pointer transition-colors duration-150"
          style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }}
        >
          <Calendar size={12} />
          {formatDate(project.deadline)}
        </button>
        {deadlinePopoverProjectId === project.id && (
          <div
            className="absolute left-0 top-full mt-2 z-[90] w-64 rounded-lg border p-3 animate-in fade-in zoom-in-95 duration-150"
            style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)", boxShadow: "var(--shadow-modal)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={(e) => shiftDeadlineCalendarMonth(e, project, -1)}
                className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer transition-colors duration-150"
                style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
                title="上个月"
              >
                <ChevronLeft size={15} />
              </button>
              <div className="text-sm font-semibold font-mono-num" style={{ color: "var(--color-text-primary)" }}>{monthLabel(calendarMonth)}</div>
              <button
                type="button"
                onClick={(e) => shiftDeadlineCalendarMonth(e, project, 1)}
                className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer transition-colors duration-150"
                style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
                title="下个月"
              >
                <ChevronRight size={15} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((day) => (
                <div key={day} className="h-7 flex items-center justify-center text-label">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day) => {
                const selected = day.value === selectedDate;
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={(e) => handleProjectMetaChange(e, project, { deadline: day.value })}
                    className="h-8 rounded-md text-xs font-mono-num cursor-pointer transition-colors duration-150"
                    style={{
                      backgroundColor: selected ? theme.colors.accentPrimary : "transparent",
                      color: selected ? "#fff" : day.currentMonth ? "var(--color-text-primary)" : "var(--color-text-muted)",
                      border: day.today && !selected ? `1px solid ${theme.colors.accentPrimary}55` : "1px solid transparent",
                      opacity: day.currentMonth ? 1 : 0.42
                    }}
                    title={day.value}
                  >
                    {day.day}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between gap-2 mt-3">
              <button
                type="button"
                onClick={(e) => handleProjectMetaChange(e, project, { deadline: "" })}
                className="h-8 px-3 rounded-md text-2xs cursor-pointer transition-colors duration-150"
                style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}
              >
                清除
              </button>
              <button
                type="button"
                onClick={(e) => handleProjectMetaChange(e, project, { deadline: todayValue })}
                className="h-8 px-3 rounded-md text-2xs cursor-pointer transition-colors duration-150"
                style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
              >
                今天
              </button>
              <button
                type="button"
                onClick={() => setDeadlinePopoverProjectId(null)}
                className="h-8 px-3 rounded-md text-2xs font-semibold cursor-pointer transition-colors duration-150"
                style={{ backgroundColor: `${theme.colors.accentPrimary}18`, color: theme.colors.accentPrimary }}
              >
                完成
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => setReleaseModalProject(project)}
          className="h-8 rounded-md px-2.5 text-2xs inline-flex items-center gap-1.5 cursor-pointer transition-colors hover:bg-[var(--color-bg-tertiary)]"
          style={{ border: '1px solid var(--color-border-primary)', color: 'var(--color-accent-primary)' }}
          aria-label={`查看${project.name}的运营状态`}
        >
          <TrendingUp size={12} />运营状态
        </button>
      </div>
    );
  };

  const ProjectCover = ({ project, compact = false }) => (
    <div className={`studio-project-cover relative rounded-md overflow-hidden shrink-0 ${compact ? "studio-project-cover--compact" : ""}`}
      style={{
        width: compact ? 72 : "100%",
        aspectRatio: "9/16",
        backgroundColor: "var(--color-bg-overlay)",
        border: "1px solid var(--color-border-secondary)"
      }}>
      {project.coverImage ? (
        <img src={project.coverImage} className="w-full h-full object-cover" alt={project.name} loading="lazy" decoding="async" />
      ) : (
        <div className="studio-project-cover__placeholder w-full h-full flex flex-col items-center justify-center" title={project.name} style={{ color: "var(--color-text-muted)" }}>
          {compact ? (
            <span className="studio-project-cover__compact-mark" aria-hidden="true"><Film size={22} strokeWidth={1.5} /></span>
          ) : (
            <>
              <StudioArtwork variant="cover" projectId={project.id} projectName={project.name} includeIdentity={false} className="studio-project-cover__art" />
              <span className="studio-project-cover__caption">
                <span className="studio-project-cover__id-label">项目 ID</span>
                <span className="studio-project-cover__id">{project.id}</span>
                <span className="studio-project-cover__empty-label">未生成封面</span>
              </span>
            </>
          )}
        </div>
      )}
      {!compact && <div className="absolute top-2 left-2">{renderStatusBadge(project)}</div>}
      {!compact && (
        <button
          onClick={(e) => handleFavorite(e, project)}
          className="studio-project-cover__favorite absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center cursor-pointer"
          aria-pressed={!!project.favorite}
          style={{
            backgroundColor: project.favorite ? theme.colors.accentPrimary : "var(--overlay-medium)",
            color: project.favorite ? "var(--color-accent-contrast)" : "color-mix(in srgb, var(--color-accent-contrast) 72%, transparent)"
          }}
          title={project.favorite ? "取消收藏" : "收藏项目"
          }
        >
          <Star size={13} fill={project.favorite ? "currentColor" : "none"} />
        </button>
      )}
      {!compact && <ProjectOperationsSummary summary={project.operationsSummary} />}
    </div>
  );

  const ProjectMarketBadge = ({ project }) => {
    if (isInteractiveGameProject(project)) return null;
    const meta = projectMarketMeta(project);
    return (
      <span
        className="studio-project-type inline-flex items-center gap-1.5 text-2xs font-medium px-2 py-1 rounded-md border shrink-0"
        style={{
          color: meta.color,
          borderColor: meta.colorBorder,
          backgroundColor: meta.colorSoft
        }}
        title={`${meta.label}项目默认使用 ${meta.subLabel}`}
      >
        {meta.label}
        <span className="font-mono-num" style={{ color: "var(--color-text-muted)" }}>{meta.subLabel}</span>
      </span>
    );
  };

  const ProjectProductionBadge = ({ project }) => {
    const repaint = isRepaintProject(project);
    const narration = isNarrationProject(project);
    const interactiveGame = isInteractiveGameProject(project);
    const sd25 = isSd25Project(project);
    const wan3 = isWan3Project(project);
    if (!narration && !repaint && !interactiveGame && !sd25 && !wan3) return null;
    const Icon = interactiveGame ? Gamepad2 : (repaint || sd25 || wan3) ? Film : ScrollText;
    const label = interactiveGame ? "互动游戏" : wan3 ? "Wan3.0 · 30 秒" : sd25 ? "30 秒剧情结构" : repaint ? "影视转绘" : "解说剧";
    return (
      <span
        className="studio-project-type inline-flex items-center gap-1 text-2xs font-medium px-2 py-1 rounded-md border shrink-0"
        style={{
          color: "var(--color-accent-primary)",
          borderColor: "color-mix(in srgb, var(--color-accent-primary) 30%, transparent)",
          backgroundColor: "color-mix(in srgb, var(--color-accent-primary) 9%, transparent)",
        }}
      >
        <Icon size={11} />
        {label}
      </span>
    );
  };

  const statItems = [
    ["项目总数", stats.total, "var(--color-text-primary)"],
    ["收藏项目", stats.favorite, "var(--color-text-primary)"],
    ["已延期", stats.overdue, stats.overdue > 0 ? "#ef4444" : "var(--color-text-primary)"],
    ["待生成封面", stats.coverless, "var(--color-text-primary)"]
  ];

  return (
    <div className="studio-project-dashboard w-full h-full flex flex-col font-sans" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      <div className="studio-project-header border-b px-7 py-5 shrink-0" style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div>
            <div className="flex items-baseline gap-3">
              <h1 className="studio-project-title text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>我的项目</h1>
              <span className="font-mono-num text-sm" style={{ color: "var(--color-text-muted)" }}>{stats.total}</span>
            </div>
            <p className="text-[13px] mt-1.5" style={{ color: "var(--color-text-muted)" }}>
              管理创作项目，查看运营状态与最新运营数据。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/annual-recap" className="studio-project-recap-entry inline-flex items-center gap-2 min-h-11 px-3 rounded-lg text-xs font-medium border">
              <Sparkles size={15} aria-hidden="true" />
              年度创作回响
            </Link>
            <OperationsEntryLink className="studio-project-ranking-entry inline-flex items-center gap-2 min-h-11 px-3 rounded-lg text-xs font-medium border focus-visible:outline-2 focus-visible:outline-offset-2" />
            <Link to="/hongguo-rankings" className="studio-project-ranking-entry inline-flex items-center gap-2 h-9 px-3 rounded-lg text-xs font-medium border">
              <TrendingUp size={15} aria-hidden="true" />
              红果榜单
            </Link>
            <button
              onClick={() => { localStorage.setItem("home_mode", "pixel"); navigate(0); }}
              className="studio-project-glass-button h-9 px-3 rounded-lg text-xs font-medium transition-colors duration-150 cursor-pointer border"
              style={{ borderColor: "var(--color-border-secondary)", color: "var(--color-text-muted)", backgroundColor: "transparent" }}
              title="切换到像素小镇首页"
            >
              小镇模式
            </button>
            <button onClick={openCreateModal} className="studio-project-new-button flex items-center gap-2 h-9 px-4 rounded-lg font-semibold text-sm cursor-pointer text-[var(--color-accent-contrast)]"
              style={{ backgroundColor: theme.colors.accentPrimary }}>
              <Plus size={15} strokeWidth={2.5} /> 新建项目
            </button>
          </div>
        </div>

        <div className="studio-project-stats rounded-lg border mt-5 overflow-hidden" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}>
          <div className="grid grid-cols-2 md:grid-cols-4">
            {statItems.map(([label, value, color], index) => (
              <div
                key={label}
                className="studio-project-stat px-4 py-3"
                style={index > 0 ? { borderLeft: "1px solid var(--color-border-secondary)" } : undefined}
              >
                <div className="studio-project-stat__label text-label">{label}</div>
                <div className="studio-project-stat__value font-mono-num text-[26px] leading-8 font-semibold mt-1" style={{ color }}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
          <div className="studio-project-segmented flex flex-wrap items-center gap-1 rounded-lg p-1 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            {FILTERS.map((item) => (
              <button
                key={item.key}
                onClick={() => setFilter(item.key)}
                className="studio-project-filter px-3 h-7 rounded-md text-xs font-medium cursor-pointer"
                aria-pressed={filter === item.key}
                style={filter === item.key
                  ? { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)" }
                  : { color: "var(--color-text-muted)" }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--color-text-muted)" }} />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="studio-project-search h-9 rounded-lg pl-9 pr-3 text-xs outline-none w-64"
                aria-label="搜索项目名称或简介"
                style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
                placeholder="搜索项目名称或简介"
              />
            </div>
            <div className="studio-project-segmented rounded-lg p-1 flex items-center gap-1 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <button onClick={() => setViewMode("grid")} className="studio-project-view w-7 h-7 rounded-md flex items-center justify-center cursor-pointer"
                aria-pressed={viewMode === "grid"}
                style={viewMode === "grid" ? { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)" } : { color: "var(--color-text-muted)" }}
                title="网格视图">
                <LayoutGrid size={15} />
              </button>
              <button onClick={() => setViewMode("list")} className="studio-project-view w-7 h-7 rounded-md flex items-center justify-center cursor-pointer"
                aria-pressed={viewMode === "list"}
                style={viewMode === "list" ? { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)" } : { color: "var(--color-text-muted)" }}
                title="列表视图">
                <ListIcon size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-7">
        {viewMode === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 pb-20">
            <button
              type="button"
              onClick={openCreateModal}
              className="studio-project-create studio-artwork-trigger rounded-lg border border-dashed flex flex-col items-center justify-center min-h-[420px] cursor-pointer border-[var(--color-border-primary)]"
            >
              <StudioArtwork variant="create" className="studio-project-create__art" />
              <span className="font-semibold text-[15px]" style={{ color: "var(--color-text-primary)" }}>创建新项目</span>
              <span className="text-label mt-1.5">短剧、转绘与互动游戏</span>
              <span className="studio-project-create__action" aria-hidden="true">
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" focusable="false">
                  <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                开始创作
              </span>
            </button>

            {filteredProjects.map((project) => (
              <Link
                key={project.id}
                to={getProjectEntryPath(project)}
                className="studio-project-card studio-artwork-trigger group relative rounded-lg border p-3 cursor-pointer block border-[var(--color-border-secondary)]"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  zIndex: deadlinePopoverProjectId === project.id ? 80 : "auto"
                }}
              >
                <ProjectCover project={project} />
                <div className="studio-project-card__details pt-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="studio-project-card__title text-[15px] font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{project.name}</h3>
                        <ProjectProductionBadge project={project} />
                        <ProjectMarketBadge project={project} />
                      </div>
                      <p className="studio-project-card__description text-xs mt-1 min-h-[34px]" style={{ color: "var(--color-text-muted)", ...cardTextClamp }}>{project.description || "暂无简介"}</p>
                    </div>
                    {project.currentUserRole === "owner" && (
                      <span className="text-label shrink-0 pt-0.5">所有者</span>
                    )}
                  </div>

                  <div className="studio-project-card__metadata flex items-center gap-2 text-2xs font-mono-num mt-3" style={{ color: "var(--color-text-muted)" }}>
                    <Clock size={11} />
                    <span>{project.createdAt ? new Date(project.createdAt).toLocaleDateString() : "未知日期"}</span>
                    <Calendar size={11} className="ml-2" />
                    <span>{formatDate(project.deadline)}</span>
                    {isProjectOverdue(project) && <span className="studio-project-overdue"><AlertTriangle size={11} />已延期</span>}
                  </div>

                  <div className="studio-project-card__footer mt-3 flex items-center justify-between gap-2">
                    {renderOwnerControls(project)}
                    <div className="flex items-center gap-1.5">
                      {project.currentUserRole === "owner" && (
                        <button onClick={(e) => openCoverModal(e, project)} className="studio-project-action w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer"
                          style={{ color: theme.colors.accentPrimary, borderColor: `${theme.colors.accentPrimary}40`, backgroundColor: `${theme.colors.accentPrimary}10` }} title="生成封面">
                          <ImagePlus size={14} />
                        </button>
                      )}
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMemberModalProject({ id: project.id, role: project.currentUserRole }); }}
                        className="studio-project-action w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }} title="成员">
                        <Users size={14} />
                      </button>
                      {project.currentUserRole === "owner" && (
                        <button onClick={(e) => handleDelete(e, project.id)} className="studio-project-action studio-project-action--danger w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer"
                          style={{ color: "var(--color-error)", borderColor: "color-mix(in srgb, var(--color-error) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-error) 7%, transparent)" }} title="删除项目">
                          <Trash2 size={14} />
                        </button>
                      )}
                      <ArrowRight size={14} className="studio-project-card__arrow ml-0.5 text-[var(--color-text-muted)]" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-2.5 pb-20">
            {filteredProjects.map((project) => (
              <Link
                key={project.id}
                to={getProjectEntryPath(project)}
                className="studio-project-card studio-project-card--list studio-artwork-trigger group relative rounded-lg border p-3 flex items-center gap-4 cursor-pointer border-[var(--color-border-secondary)]"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  zIndex: deadlinePopoverProjectId === project.id ? 80 : "auto"
                }}
              >
                <ProjectCover project={project} compact />
                <div className="studio-project-card__list-info flex-1 min-w-0">
                  <div className="studio-project-card__list-title flex items-center gap-2">
                    <h3 className="studio-project-card__title text-[15px] font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{project.name}</h3>
                    <ProjectProductionBadge project={project} />
                    <ProjectMarketBadge project={project} />
                    {project.currentUserRole === "owner" && <span className="text-label shrink-0">所有者</span>}
                  </div>
                  <p className="studio-project-card__description text-xs mt-1 truncate" style={{ color: "var(--color-text-muted)" }}>{project.description || "暂无简介"}</p>
                  <div className="studio-project-card__metadata flex items-center gap-3 mt-2 text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>
                    <span>创建：{project.createdAt ? new Date(project.createdAt).toLocaleDateString() : "未知"}</span>
                    {renderStatusBadge(project)}
                    {isProjectOverdue(project) && <span className="studio-project-overdue"><AlertTriangle size={11} />已延期</span>}
                  </div>
                  <ProjectOperationsSummary summary={project.operationsSummary} compact />
                </div>
                <div className="studio-project-card__list-actions flex items-center gap-2">
                  {renderOwnerControls(project)}
                  {project.currentUserRole === "owner" && (
                    <button onClick={(e) => openCoverModal(e, project)} className="studio-project-action h-8 px-3 rounded-md text-xs font-semibold border cursor-pointer"
                      style={{ color: theme.colors.accentPrimary, borderColor: `${theme.colors.accentPrimary}40`, backgroundColor: `${theme.colors.accentPrimary}10` }}>
                      生成封面
                    </button>
                  )}
                  <button onClick={(e) => handleFavorite(e, project)} className="studio-project-action w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer"
                    aria-pressed={!!project.favorite}
                    style={{ color: project.favorite ? theme.colors.accentPrimary : "var(--color-text-muted)", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}
                    title={project.favorite ? "取消收藏" : "收藏项目"}>
                    <Star size={14} fill={project.favorite ? "currentColor" : "none"} />
                  </button>
                  <ArrowRight size={15} className="studio-project-card__arrow text-[var(--color-text-muted)]" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {filteredProjects.length === 0 && !isCreating && (
          <div className="studio-project-empty rounded-lg border border-dashed h-64 flex flex-col items-center justify-center gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
            <StudioArtwork variant="empty" className="studio-project-empty__art" />
            <div className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>
              {projects.length === 0 ? "还没有项目" : "没有匹配的项目"}
            </div>
            <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {projects.length === 0 ? "从新建项目开始，选择适合你的创作方式。" : "调整搜索关键词或筛选条件。"}
            </div>
            {projects.length === 0 && (
              <button onClick={openCreateModal} className="mt-2 h-8 px-4 rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                style={{ color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}40`, backgroundColor: `${theme.colors.accentPrimary}10` }}>
                新建项目
              </button>
            )}
          </div>
        )}
      </div>

      {createModalVisibility.mounted && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-50 ${createModalVisibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}
          style={modalOverlay}
          onAnimationEnd={(event) => {
            if (event.target !== event.currentTarget || !createModalVisibility.exiting || !resetCreateFormAfterExitRef.current) return;
            resetCreateFormAfterExitRef.current = false;
            resetNewProjectForm();
          }}
        >
          <div className={`relative rounded-xl w-[960px] max-w-[94vw] h-[min(860px,94vh)] overflow-hidden flex flex-col border ${createModalVisibility.exiting ? "ui-modal-exit" : "ui-modal-enter"}`} style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)", boxShadow: "var(--shadow-modal)" }}>
            <div className="flex items-center justify-between px-7 pt-6 pb-4 shrink-0">
              <div>
                <h2 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>创建新项目</h2>
                <p className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>选择项目类型后，将进入对应的创作工作台。</p>
              </div>
              <button onClick={() => setIsCreating(false)} className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer transition-colors duration-150 hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}
                title="关闭">
                <X size={17} />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-7 pb-6">
              <div className="space-y-5">
                <div>
                  <label className="text-label block mb-2">项目名称</label>
                  <input className="w-full rounded-lg px-4 py-3 text-sm outline-none" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }} placeholder="例如：十年账单" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} autoFocus />
                </div>
                <div>
                  <label className="text-label block mb-2">简介</label>
                  <textarea className="w-full rounded-lg px-4 py-3 text-sm outline-none resize-none h-24" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }} placeholder="一句话说明这个项目（可选）" value={newProjectDesc} onChange={(e) => setNewProjectDesc(e.target.value)} />
                </div>
                <div>
                  <label className="text-label block mb-2">项目类型</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      {
                        value: PROJECT_PRODUCTION_MODE_STANDARD,
                        title: "标准短剧",
                        desc: "使用完整项目工具，自由安排剧本、资产、故事板与视频制作",
                      },
                      {
                        value: PROJECT_PRODUCTION_MODE_SD25,
                        title: "即梦 2.5 项目",
                        desc: "小故事板按 30 秒连续剧情组织；具体生成时再切换 2.5 厂商线路，参考图不使用素材库 ID",
                      },
                      {
                        value: PROJECT_PRODUCTION_MODE_WAN3,
                        title: "Wan3.0 项目",
                        desc: "全项目固定使用万相 3.0，按 30 秒独立提示词策略组织小故事板，镜头数量由剧情决定",
                      },
                      {
                        value: PROJECT_PRODUCTION_MODE_NARRATION,
                        title: "解说剧生产",
                        desc: "导入解说剧剧本，确认资产和分镜后进入批量视频生产",
                      },
                      {
                        value: PROJECT_PRODUCTION_MODE_REPAINT,
                        title: "影视转绘",
                        desc: "导入中文参考视频，识别人景并映射为英文角色与场景",
                      },
                      {
                        value: PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME,
                        title: "互动游戏",
                        desc: "用分支剧情画布编写角色路线、选择与多结局，并进行文字预演",
                      },
                    ].map((item) => {
                      const active = newProjectProductionMode === item.value;
                      return (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => {
                            setNewProjectProductionMode(item.value);
                            if (item.value === PROJECT_PRODUCTION_MODE_REPAINT) {
                              setNewProjectMarket("overseas");
                              setNewProjectVideoProvider(VIDEO_PROVIDER_ENG_GUAVIDEO);
                            } else if (item.value === PROJECT_PRODUCTION_MODE_SD25) {
                              setNewProjectMarket("domestic");
                            } else if (item.value === PROJECT_PRODUCTION_MODE_WAN3) {
                              setNewProjectMarket("domestic");
                              setNewProjectVideoProvider(VIDEO_PROVIDER_WANX);
                              if (newProjectVideoRatio === "21:9") setNewProjectVideoRatio(PROJECT_DEFAULT_VIDEO_RATIO);
                            } else if (isSeedance25Provider(newProjectVideoProvider) || newProjectVideoProvider === VIDEO_PROVIDER_WANX) {
                              setNewProjectMarket("domestic");
                              setNewProjectVideoProvider(VIDEO_PROVIDER_GUAVIDEO);
                            }
                          }}
                          aria-pressed={active}
                          className="text-left rounded-lg px-3 py-3 border transition-colors duration-150 cursor-pointer"
                          style={active
                            ? {
                              borderColor: theme.colors.accentPrimary,
                              backgroundColor: `${theme.colors.accentPrimary}12`,
                              color: "var(--color-text-primary)",
                            }
                            : {
                              borderColor: "var(--color-border-primary)",
                              backgroundColor: "var(--color-bg-overlay)",
                              color: "var(--color-text-secondary)",
                            }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0"
                              style={{ borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)" }}
                            >
                              {active && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.colors.accentPrimary }} />}
                            </span>
                            <div className="text-sm font-semibold">{item.title}</div>
                          </div>
                          <div className="text-2xs mt-1.5 pl-[22px] leading-5" style={{ color: "var(--color-text-muted)" }}>{item.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {newProjectProductionMode !== PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME && (
                  <>
                    {newProjectProductionMode === PROJECT_PRODUCTION_MODE_SD25 ? (
                      <div className="rounded-lg border px-4 py-3 text-xs leading-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-muted)" }}>
                        这里只确定“即梦 2.5 项目”的 30 秒分镜规则。Seedance 2.5（国内官方）、Dreamina 2.5（海外代理）、AKOOL、New API、Astroflow 等独立厂商线路，在小故事板、故事板导演台或视频生成页实际生成时选择。
                      </div>
                    ) : newProjectProductionMode === PROJECT_PRODUCTION_MODE_WAN3 ? (
                      <div className="rounded-lg border px-4 py-3 text-xs leading-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-muted)" }}>
                        Wan3.0 项目固定走万相 3.0 独立提示词与 30 秒剧情结构。标准版或高速版在实际生成时选择；参考素材直接传 URL，不使用 Seedance 素材库 ID。
                      </div>
                    ) : (
                    <div>
                      <label className="text-label block mb-2">发行区域</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[
                      { market: 'domestic', provider: VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, title: '国内官方项目', desc: 'Seedance 2.0 / Fast / Mini · 中文提示词' },
                      { market: "domestic", provider: VIDEO_PROVIDER_GUAVIDEO, title: "国内项目", desc: "GuaVideo · 中文提示词" },
                      { market: "overseas", provider: VIDEO_PROVIDER_ENG_GUAVIDEO, title: "出海项目", desc: "EngGuaVideo · 英文提示词" },
                      { market: "overseas", provider: VIDEO_PROVIDER_OMINI_SEEDANCE, title: "出海项目", desc: "GuaOmini · 英文提示词" },
                    ].map((item) => {
                      const active = newProjectMarket === item.market && newProjectVideoProvider === item.provider;
                      const disabled = newProjectProductionMode === PROJECT_PRODUCTION_MODE_REPAINT && item.provider !== VIDEO_PROVIDER_ENG_GUAVIDEO;
                      const color = item.provider === VIDEO_PROVIDER_OMINI_SEEDANCE
                        ? "var(--color-info, #38bdf8)"
                        : item.market === "overseas" ? "var(--color-success)" : theme.colors.accentPrimary;
                      const colorSoft = `color-mix(in srgb, ${color} 7%, transparent)`;
                      return (
                        <button
                          key={item.provider}
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            if (!disabled) {
                              setNewProjectMarket(item.market);
                              setNewProjectVideoProvider(item.provider);
                            }
                          }}
                          className="text-left rounded-lg px-3 py-3 border transition-colors duration-150 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                          style={active
                            ? { borderColor: color, backgroundColor: colorSoft, color: "var(--color-text-primary)" }
                            : { borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-secondary)" }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0"
                              style={{ borderColor: active ? color : "var(--color-border-primary)" }}
                            >
                              {active && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />}
                            </span>
                            <div className="text-sm font-semibold">{item.title}</div>
                          </div>
                          <div className="text-2xs mt-1.5 pl-[22px]" style={{ color: "var(--color-text-muted)" }}>{item.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                  {newProjectProductionMode === PROJECT_PRODUCTION_MODE_REPAINT && (
                    <p className="mt-2 text-2xs" style={{ color: "var(--color-success)" }}>
                      影视转绘使用出海视频线路，并按英文内容生成。
                    </p>
                  )}
                    </div>
                    )}
                    <div>
                      <label className="text-label block mb-2">默认视频比例</label>
                      <div className="grid grid-cols-3 gap-2">
                        {PROJECT_VIDEO_RATIOS.filter((ratio) => newProjectProductionMode !== PROJECT_PRODUCTION_MODE_WAN3 || ratio !== "21:9").map((ratio) => {
                          const active = newProjectVideoRatio === ratio;
                          return (
                            <button
                              key={ratio}
                              type="button"
                              onClick={() => setNewProjectVideoRatio(ratio)}
                              aria-pressed={active}
                              className="h-16 rounded-lg border flex items-center justify-center gap-3 transition-colors duration-150 cursor-pointer"
                              style={active
                                ? {
                                  borderColor: theme.colors.accentPrimary,
                                  backgroundColor: `${theme.colors.accentPrimary}12`,
                                  color: "var(--color-text-primary)",
                                }
                                : {
                                  borderColor: "var(--color-border-primary)",
                                  backgroundColor: "var(--color-bg-overlay)",
                                  color: "var(--color-text-secondary)",
                                }}
                              title={`项目默认视频比例 ${ratio}`}
                            >
                              <span
                                className="block rounded-sm border shrink-0"
                                style={{
                                  width: ratio === "9:16" ? 15 : ratio === "21:9" ? 34 : 27,
                                  height: ratio === "9:16" ? 27 : ratio === "21:9" ? 15 : 15,
                                  borderColor: active ? theme.colors.accentPrimary : "var(--color-border-hover)",
                                  backgroundColor: active ? `${theme.colors.accentPrimary}18` : "var(--color-bg-tertiary)",
                                }}
                              />
                              <span className="text-sm font-semibold font-mono-num">{ratio}</span>
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-2xs" style={{ color: "var(--color-text-muted)" }}>
                        创建后会直接保存为项目默认画幅，故事板和视频生成将优先使用这个比例。
                      </p>
                    </div>
                    <div>
                      <label className="text-label block mb-2">项目画风</label>
                  <div className="grid grid-cols-2 gap-3 pb-2">
                      <button
                        type="button"
                        onClick={() => setNewProjectVisualStyleMode("none")}
                        aria-pressed={newProjectVisualStyleMode === "none"}
                        className="relative w-full self-start text-left rounded-lg border transition-colors duration-150 cursor-pointer overflow-hidden"
                        style={newProjectVisualStyleMode === "none"
                          ? { aspectRatio: "16 / 9", borderColor: theme.colors.accentPrimary, backgroundColor: `${theme.colors.accentPrimary}12`, color: "var(--color-text-primary)" }
                          : { aspectRatio: "16 / 9", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-secondary)" }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-sm font-semibold">无固定风格</div>
                        </div>
                        {newProjectVisualStyleMode === "none" && (
                          <div className="absolute right-2.5 top-2.5 w-5 h-5 rounded-md flex items-center justify-center text-[var(--color-accent-contrast)]" style={{ backgroundColor: theme.colors.accentPrimary }}>
                            <Check size={13} />
                          </div>
                        )}
                      </button>
                      {visualStyleOptions.filter((item) => item.value !== "custom").map((item) => {
                        const active = newProjectVisualStyleMode === "fixed" && newProjectVisualStyle === item.value;
                        return (
                          <button
                            key={item.value}
                            type="button"
                            onClick={() => {
                              setNewProjectVisualStyleMode("fixed");
                              setNewProjectVisualStyle(item.value);
                            }}
                            aria-pressed={active}
                            className="relative w-full self-start text-left rounded-lg border transition-colors duration-150 cursor-pointer overflow-hidden"
                            style={active
                              ? { display: "block", boxSizing: "border-box", borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)" }
                              : { display: "block", boxSizing: "border-box", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-secondary)" }}
                          >
                            {item.referenceImageUrl ? (
                              <div className="relative w-full flex items-center justify-center" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                                <img
                                  src={item.referenceImageUrl}
                                  alt={`${item.label}参考图`}
                                  loading="lazy"
                                  decoding="async"
                                  className="block w-full h-auto"
                                />
                              </div>
                            ) : (
                              <div className="relative w-full" style={{ aspectRatio: "16 / 9", backgroundColor: "var(--color-bg-tertiary)" }} />
                            )}
                            <div
                              className="absolute inset-x-0 bottom-0 px-3 py-2.5 flex items-center justify-between gap-3"
                              style={{
                                backgroundColor: "var(--overlay-heavy)",
                                borderTop: "1px solid color-mix(in srgb, var(--color-accent-contrast) 8%, transparent)"
                              }}
                            >
                              <div className="text-sm font-semibold truncate text-white">{item.label}</div>
                              {active && (
                                <div className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 text-[var(--color-accent-contrast)]" style={{ backgroundColor: theme.colors.accentPrimary }}>
                                  <Check size={13} />
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                  </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="shrink-0 flex justify-end items-center gap-3 px-7 py-4 border-t" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
              <button onClick={() => setIsCreating(false)} className="h-9 px-4 text-sm rounded-lg cursor-pointer transition-colors duration-150" style={{ color: "var(--color-text-muted)" }}>取消</button>
              <button onClick={handleCreate} className="h-9 px-5 rounded-lg font-semibold text-sm cursor-pointer text-[var(--color-accent-contrast)] transition-colors duration-150" style={{ backgroundColor: theme.colors.accentPrimary }}>创建</button>
            </div>
          </div>
        </div>
      )}

      {coverModalProject && (
        <div className="fixed inset-0 flex items-center justify-center z-50 animate-in fade-in duration-150" style={modalOverlay}>
          <div className="rounded-xl w-[1120px] max-w-[94vw] max-h-[90vh] overflow-hidden border flex animate-in fade-in zoom-in-95 duration-200" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)", boxShadow: "var(--shadow-modal)" }}>
            <div className="w-[320px] border-r p-5 overflow-y-auto custom-scrollbar" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>项目封面</h2>
                  <p className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>固定 9:16，只有创建者可生成。</p>
                </div>
                <button onClick={() => setCoverModalProject(null)} className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer transition-colors duration-150 hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}
                  title="关闭">
                  <X size={17} />
                </button>
              </div>

              <div className="rounded-md overflow-hidden border mb-4" style={{ aspectRatio: "9/16", borderColor: "var(--color-border-secondary)", backgroundColor: "var(--color-bg-overlay)" }}>
                {coverModalProject.coverImage ? (
                  <img src={coverModalProject.coverImage} className="w-full h-full object-cover" alt="项目封面" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ color: "var(--color-text-muted)" }}>
                    <ImagePlus size={30} strokeWidth={1.5} />
                    <span className="text-label">未生成封面</span>
                  </div>
                )}
              </div>

              <label className="text-label block mb-2">封面最终提示词</label>
              <textarea
                value={coverPrompt}
                onChange={(e) => setCoverPrompt(e.target.value)}
                className="w-full min-h-[130px] rounded-lg p-3 text-sm outline-none resize-none"
                style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
              />
              <p className="text-2xs mt-2" style={{ color: "var(--color-text-muted)" }}>
                这里会作为最终提示词提交；系统只额外按你选择的顺序附加参考图。
              </p>
              <button
                onClick={generateCover}
                disabled={generatingCover}
                className="w-full mt-4 h-10 rounded-lg font-semibold text-sm text-[var(--color-accent-contrast)] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 transition-colors duration-150"
                style={{ backgroundColor: theme.colors.accentPrimary }}
              >
                {generatingCover ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
                {generatingCover ? "正在生成封面" : "生成 9:16 封面"}
              </button>
            </div>

            <div className="flex-1 p-5 overflow-y-auto custom-scrollbar">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>选择参考图</h3>
                  <p className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                    从当前项目的角色、场景、道具工坊中手动选择。已选 <span className="font-mono-num" style={{ color: "var(--color-text-secondary)" }}>{coverSelectedRefs.length}</span> 张{isWanxCoverProvider ? ` / 最多 ${WANX_IMAGE_MAX_INPUT_IMAGES} 张，按2K输出` : ""}。
                  </p>
                </div>
              </div>

              <div className="inline-flex flex-wrap items-center gap-1 rounded-lg p-1 border mb-4" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
                {COVER_ASSET_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setCoverAssetTab(tab.key)}
                    className="px-3 h-7 rounded-md text-xs font-medium transition-colors duration-150 cursor-pointer"
                    style={coverAssetTab === tab.key
                      ? { backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)" }
                      : { color: "var(--color-text-muted)" }}
                  >
                    {tab.label} <span className="font-mono-num">{(coverAssets[tab.key] || []).length}</span>
                  </button>
                ))}
              </div>

              {coverSelectedRefs.length > 0 && (
                <div className="rounded-lg border p-3 mb-4" style={{ borderColor: "var(--color-border-secondary)", backgroundColor: "var(--color-bg-secondary)" }}>
                  <div className="text-label mb-2">最终上传顺序</div>
                  <div className="flex flex-wrap gap-2">
                    {coverSelectedRefs.map((asset, index) => (
                      <button
                        key={asset.key}
                        onClick={() => setCoverSelectedRefs((prev) => prev.filter((item) => item.key !== asset.key))}
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-2xs font-semibold border cursor-pointer transition-colors duration-150"
                        style={{ color: theme.colors.accentPrimary, borderColor: `${theme.colors.accentPrimary}45`, backgroundColor: `${theme.colors.accentPrimary}10` }}
                        title="点击移除"
                      >
                        <span className="font-mono-num">图{index + 1}</span> {asset.label || asset.name || "参考图"}
                        <X size={11} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loadingCoverAssets ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div key={index}>
                      <div className="skeleton aspect-[4/3]" />
                      <div className="skeleton h-3 w-2/3 mt-2" />
                      <div className="skeleton w-1/3 mt-1.5" style={{ height: 10 }} />
                    </div>
                  ))}
                </div>
              ) : allCoverAssets.length === 0 ? (
                <div className="h-64 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
                  <ImagePlus size={22} strokeWidth={1.5} />
                  <div className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>还没有可用参考图</div>
                  <div className="text-xs">先在角色、场景或道具工坊中生成图片。</div>
                </div>
              ) : visibleCoverAssets.length === 0 ? (
                <div className="h-64 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
                  <div className="text-sm font-medium" style={{ color: "var(--color-text-secondary)" }}>当前分类还没有图片</div>
                  <div className="text-xs">切换到其他分类看看。</div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {visibleCoverAssets.map((asset) => {
                    const selectedIndex = coverSelectedRefs.findIndex((item) => item.key === asset.key);
                    const selected = selectedIndex >= 0;
                    const selectionBlocked = isWanxCoverProvider
                      && !selected
                      && coverSelectedRefs.length >= WANX_IMAGE_MAX_INPUT_IMAGES;
                    return (
                      <button
                        key={asset.key}
                        onClick={() => toggleCoverRef(asset)}
                        aria-disabled={selectionBlocked}
                        title={selectionBlocked ? `千问图像3.0最多选择${WANX_IMAGE_MAX_INPUT_IMAGES}张参考图` : undefined}
                        className={`group text-left rounded-lg border overflow-hidden transition-colors duration-150 ${selectionBlocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                        style={{
                          borderColor: selected ? theme.colors.accentPrimary : "var(--color-border-secondary)",
                          backgroundColor: selected ? `${theme.colors.accentPrimary}10` : "var(--color-bg-secondary)"
                        }}
                      >
                        <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                          <img src={asset.imageUrl} className="w-full h-full object-cover" alt={asset.label} />
                        </div>
                        <div className="p-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-label">{asset.typeLabel}</span>
                            {selected && (
                              <span className="inline-flex items-center gap-1 text-2xs font-semibold font-mono-num" style={{ color: theme.colors.accentPrimary }}>
                                图{selectedIndex + 1}
                                <CheckCircle2 size={13} />
                              </span>
                            )}
                          </div>
                          <div className="text-xs font-semibold truncate mt-1" style={{ color: "var(--color-text-primary)" }}>{asset.name || "未命名"}</div>
                          <div className="text-2xs truncate mt-0.5" style={{ color: "var(--color-text-muted)" }}>{asset.variantTitle || "主图"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {releaseModalProject && (
        <Suspense fallback={<div className="fixed bottom-6 right-6 z-[120] rounded-lg p-4" role="status" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>正在打开运营状态…</div>}>
          <ProjectReleaseDialog key={releaseModalProject.id} project={releaseModalProject} onReleaseChange={release => {
            if (release?.status) setProjects(current => current.map(project => project.id === releaseModalProject.id
              ? { ...project, operationsSummary: { ...project.operationsSummary, status: release.status } } : project));
            fetchProjects({ silent: true, force: true });
          }} onClose={() => { setReleaseModalProject(null); fetchProjects({ silent: true }); }} />
        </Suspense>
      )}
      {memberModalProject && (
        <ProjectMemberModal
          projectId={memberModalProject.id}
          userId={userId}
          canManage={memberModalProject.role === "owner"}
          onClose={() => setMemberModalProject(null)}
        />
      )}
    </div>
  );
};

export default ProjectDashboard;
